<template>
  <div v-if="!isLoading" class="dashboard">
    <!-- Overview -->
    <div class="row">
      <div class="col-lg-3 col-sm-6">
        <a class="text-black" @click="load24HData()">
          <va-info-box
            :sub-text="dashboardOverview24H.TotalPaidTransactions + ' / ' + dashboardOverview24H.TotalTransactions"
            bgColor="bg-aqua"
            bgIcon="ion ion-cash"
            text="Transactions / 24H"></va-info-box>
        </a>
      </div>
      <div class="col-lg-3 col-sm-6">
        <va-info-box
          :sub-text="dashboardOverview24H.TotalPaidVolume + ' / ' + dashboardOverview24H.TotalVolume"
          bgColor="bg-red"
          bgIcon="fa ion-stats-bars"
          text="Volume (EUR) / 24H"></va-info-box>
      </div>
      <div class="col-lg-3 col-sm-6">
        <va-info-box
          :number="dashboardOverview24H.TotalRevenueInEUR"
          bgColor="bg-green"
          bgIcon="ion ion-social-euro"
          text="Revenue (EUR) / 24H"></va-info-box>
      </div>
      <div class="col-lg-3 col-sm-6">
        <va-info-box
          :number="dashboardOverview24H.TotalRegistrations"
          bgColor="bg-yellow"
          bgIcon="ion ion-ios-people"
          text="New Registrations / 24H"></va-info-box>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-9">
        <!-- Last 24H Transactions -->
        <div class="box box-info">
          <div class="box-header">
            <div class="flex align-items-center justify-between">
              <div v-if="sync !== ''" class="text-bold text-danger ml-8 flex align-items-center"
                   v-html="this.sync"></div>
              <label class="mb-0 ml-8">From</label>
              <date-picker v-model="range.startDate" :clearable="false" :editable="false" class="ml-8"
                           :disabled-date="disabledDates"
                           format="YYYY-MM-DD" value-type="format"></date-picker>
              <label class="mb-0 ml-8">To</label>
              <date-picker v-model="range.endDate" :clearable="false" :editable="false" class="ml-8"
                           :disabled-date="disabledDates"
                           format="YYYY-MM-DD" value-type="format"></date-picker>
              <button class="btn btn-primary btn-sm btn-flat ml-8" @click="searchLatestInfo()">Limit Date Range</button>
              <v-select v-model="merchantId" :options="merchants" class="ml-8" style="min-width: 200px"
                        :reduce="m => m.ID" label="Name"></v-select>
              <input ref="search" v-model="keyword" aria-label="Search" id="search"
                     class="form-control w-150 ml-8" placeholder="Search..." type="search"/>
              <button class="btn btn-primary btn-sm btn-flat ml-8" @click="resetPage()">[X]</button>
              <select v-model="status" aria-label="Status" class="form-control w-150 ml-8">
                <option v-for="(s, index) in Object.keys(statusList)" :key="index"
                        :value="s">{{ statusList[s] }}
                </option>
              </select>
            </div>
          </div>
          <div class="box-body no-padding">
            <div class="table-responsive">
              <table class="table table-condensed table-striped mb-0">
                <thead>
                <tr>
                  <th class="pointer" @click="sort('OwnerNickName')">
                    <span class="flex align-items-center justify-between">
                      Merchant
                      <span v-if="sortKey === 'OwnerNickName'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="pointer" @click="sort('TransactionID')">
                    <span class="flex align-items-center justify-between">
                      Transaction ID
                      <span v-if="sortKey === 'TransactionID'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="text-right pointer" @click="sort('OriginalAmount')">
                    <span class="flex align-items-center justify-between">
                      Original Amount
                      <span v-if="sortKey === 'OriginalAmount'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="pointer" @click="sort('Amount')">
                    <span class="flex align-items-center justify-between">
                      Amount
                      <span v-if="sortKey === 'Amount'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="pointer" @click="sort('ExternalTransactionProvider')">
                    <span class="flex align-items-center justify-between">
                      Type
                      <span v-if="sortKey === 'ExternalTransactionProvider'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="pointer" @click="sort('Status')">
                    <span class="flex align-items-center justify-between">
                      Status
                      <span v-if="sortKey === 'Status'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="pointer" @click="sort('CreatedOn')">
                    <span class="flex align-items-center justify-between">
                      Created On
                      <span v-if="sortKey === 'CreatedOn'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                  <th class="pointer" @click="sort('TransactionConfirmedOn')">
                    <span class="flex align-items-center justify-between">
                      Confirmed On
                      <span v-if="sortKey === 'TransactionConfirmedOn'" class="ml-4">
                        <i class="fa fa-sort"></i>
                      </span>
                    </span>
                  </th>
                </tr>
                </thead>
                <tbody>
                <template v-if="paginatedTransactions.length > 0">
                  <tr v-for="(transaction, index) in paginatedTransactions" :key="index">
                    <td class="no-wrap">
                      <router-link :to="{ name: 'user-detail', params: { id: transaction.MerchantID } }" tag="a">
                        {{ transaction.OwnerNickName + ' - ' + transaction.ShopTitle }}
                      </router-link>
                    </td>
                    <td style="min-width:150px;">
                      <router-link
                        :to="{name: 'transaction-detail', params:{ id: transaction.Prefix + transaction.TransactionID.toString().padStart(9, '0') }}"
                        class="d-inline-block" tag="a"
                        @click.native="savePage()">
                        {{ transaction.Prefix + transaction.TransactionID.toString().padStart(9, '0') }}
                      </router-link>
                      <span class="ml-8 pointer d-inline-block" @click="copyToClipboard(transaction.TransactionID)">
                        <i class="fa fa-clipboard"></i>
                      </span>
                      <span v-if="transaction.Prefix === 'T'" class="ml-8 pointer d-inline-block"
                            @click="copyToClipboard(transaction.Security, false)">
                        <i class="fa fa-key"></i>
                      </span>
                      <span v-if="transaction.Prefix === 'T'" class="ml-8 pointer d-inline-block"
                            @click="copyToClipboard(transaction.Security, true)">
                        <i class="fa fa-globe"></i>
                      </span>
                      <span v-if="transaction.Prefix === 'T'" class="ml-8 pointer d-inline-block"
                            @click="copyToClipboard(transaction.CustomerReferenceNr)">
                        <i class="fa fa-user"></i>
                      </span>
                    </td>
                    <td class="nowrap" align="right">
                      {{ transaction.OriginalAmount.toFixed(8) + ' ' + transaction.InputCurrency }}
                        <span class="ml-8">
                          <i class="fa fa-copy pointer"
                             @click="toConverter(toFixed(transaction.OriginalAmount.toFixed(2)), shortNameToID(transaction.InputCurrency))"></i>
                        </span>
                    </td>
                    <td class="nowrap" align="right">
                        {{ transaction.Amount.toFixed(8) + ' ' + transaction.LongName }}
                        <span class="ml-8">
                          <i class="fa fa-copy pointer"
                             @click="toConverter(transaction.Amount, transaction.AltCoinID)"></i>
                        </span>
                    </td>
                    <td class="nowrap">{{
                        transaction.ExternalTransactionProvider ? transaction.ExternalTransactionProvider : transaction.TransactionType
                      }}
                    </td>
                    <td>
                      <span class="flex align-items-center">
                        <span v-if="transaction.Prefix !== 'P'" @click="launchTransactionStatusModal(transaction)"
                              v-html="fs(transaction.Status)" class="mr-8"></span>
                        <span v-if="transaction.Prefix === 'P'"
                              v-html="fs(transaction.Status)" class="mr-8"></span>
                        <i class="fa fa-comments-o fa-2x pointer"
                           @click="launchUpdateTransactionLogsModal(transaction, index)"></i>
                        <a @click="getRevolutTransactionDetail(transaction.Security)" class="ml-8 text-black" v-if="getType(transaction) === 'RevolutCard'">
                          <i class="fa fa-credit-card fa-2x"
                             :class="{'text-dark-gray': transaction.ExternalTransactionProvider !== 'RevolutCard' && (transaction.Status === 'waiting' || transaction.Status === 'expired')}"></i>
                        </a>
                        <a v-if="getType(transaction) === 'Bunq'" @click="getBunqTransactionDetail(transaction)" class="ml-8">
                          <img style="height: 20px;" src="@/assets/bunq.svg" alt=""/>
                        </a>
                        <a @click="getSquareTransactionDetail(transaction)" class="ml-8" v-if="transaction.revolutID && getType(transaction) === 'Squareup'">
                          <img style="height: 20px;" src="@/assets/square.png" alt=""/>
                        </a>
                        <a @click="getStripeTransactionDetail(transaction)" class="ml-8" v-if="transaction.revolutID && getType(transaction) === 'Stripe'">
                          <i class="fa fa-cc-stripe fa-2x"></i>
                        </a>
                        <a @click="getRevolutBankTransactionDetail(transaction)"
                           class="ml-8 text-black"
                           v-if="getType(transaction) === 'RevolutBank' && transaction.revolutID">
                          <i class="fa fa-bank"></i>
                        </a>
                        <a @click="getRapydTransactionDetail(transaction)" class="ml-8" v-if="transaction.revolutID && getType(transaction) === 'Rapyd'">
                          <img style="height: 20px;" src="@/assets/rapyd.png" alt=""/>
                        </a>
                        <a @click="getPayrexxTransactionDetail(transaction)" class="ml-8" v-if="transaction.revolutID && getType(transaction) === 'Payrexx'">
                          <i class="fa fa-mug-hot"></i>
                        </a>
                      </span>
                    </td>
                    <td class="nowrap">{{ fd(transaction.CreatedOn) }}</td>
                    <td class="nowrap">{{ fd(transaction.TransactionConfirmedOn) }}</td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td colspan="8">No transaction available</td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>
            <div v-if="filteredTransactions && filteredTransactions.length" class="footer">
              <pagination :meta="meta" @pageChange="updatePage"></pagination>
            </div>
          </div>
        </div>
        <div class="box box-info" v-if="transactionFailureCauses">
          <div class="box-header">
            <h3 class="box-title">Transaction Failure</h3>
          </div>
          <div class="box-body no-padding">
            <div class="table-responsive">
              <table class="table table-condensed table-striped mb-0">
                <thead>
                <tr>
                  <th>ID</th>
                  <th>AltCoin</th>
                  <th>Failure</th>
                  <th>Address</th>
                  <th>CreatedOn</th>
                  <th>Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(record, index) in transactionFailureCauses.records" :key="index">
                  <td>{{ record.ID }}</td>
                  <td class="no-wrap">{{ record.Name + ' - ' + record.LongName }}</td>
                  <td>{{ record.Failure }}</td>
                  <td>{{ record.Address }}</td>
                  <td class="no-wrap">{{ fd(record.CreatedOn) }}</td>
                  <td style="min-width: 200px">
                    <router-link class="btn btn-primary btn-sm btn-flat mr-8"
                                 tag="a" :to="{ name: 'coin-health-detail', params: { id: record.AltCoinID } }">
                      Balance Sheet
                    </router-link>
                    <router-link class="btn btn-primary btn-sm btn-flat"
                                 tag="a"
                                 :to="{ name: 'xchange-offline-pool', query: { coin: record.Name, id: record.AltCoinID } }">
                      Offline Pool
                    </router-link>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <div v-if="transactionFailureCauses && transactionFailureCauses.records.length" class="footer">
              <pagination :meta="transactionFailureCauses.meta" @pageChange="updatePageTF"></pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3">
        <div class="box box-warning">
          <div class="box-header">
            <h3 class="box-title">Pending KYC For Review</h3>
          </div>
          <div class="box-body no-padding">
            <div class="table-responsive">
              <table class="table table-condensed table-striped">
                <thead>
                <tr>
                  <th>Merchant</th>
                  <th style="min-width: 160px;width: 160px">Last Updated On</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="pendingKYC.length > 0">
                  <tr v-for="(kyc, index) in pendingKYC" :key="index">
                    <td style="word-break: break-all;">
                      <router-link :to="{ name: 'user-detail', params: { id: kyc.ID } }" tag="a">
                        {{ kyc.ShopTitle }}
                      </router-link>
                    </td>
                    <td>{{ fd(kyc.LastUpdatedOn) }}</td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td colspan="2">No pending KYC found</td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="box box-primary">
          <div class="box-header">
            <h3 class="box-title">New Registrations</h3>
          </div>
          <div class="box-body no-padding">
            <div class="table-responsive">
              <table class="table table-condensed table-striped">
                <thead>
                <tr>
                  <th>Name</th>
                  <th style="min-width: 160px;width: 160px">Created On</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="recentRegistrations.length > 0">
                  <tr v-for="(registration, index) in recentRegistrations" :key="index">
                    <td class="no-wrap" style="word-break: break-all;">
                      <router-link :to="{ name: 'user-detail', params: { id: registration.ID } }" tag="a">
                        {{ registration.Name }}
                      </router-link>
                    </td>
                    <td class="no-wrap">{{ fd(registration.RegisteredOn) }}</td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td colspan="2">No registration found</td>
                  </tr>
                </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Update transaction status modal -->
    <update-transaction-status-modal></update-transaction-status-modal>
    <!-- Update transaction logs modal -->
    <update-transaction-logs-modal></update-transaction-logs-modal>
    <!-- Revolut Result Modal -->
    <revolut-result-modal></revolut-result-modal>
    <revolut-bank-result-modal></revolut-bank-result-modal>
    <!-- Bunq Result Modal -->
    <bunq-result-modal></bunq-result-modal>
    <!-- Square Result Modal -->
    <square-result-modal></square-result-modal>
    <!-- Stripe Result Modal -->
    <stripe-result-modal></stripe-result-modal>
    <!-- Rapyd Result Modal -->
    <rapyd-result-modal></rapyd-result-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import VAInfoBox from '../widgets/VAInfoBox.vue'
import Pagination from '@/components/Pagination.vue'
import { dateDiffInDays, formatDate, formatStatus, saveNotification, toFixed } from '@/lib/utils'
import {
  ROLES,
  SAVE_CONVERTER,
  SAVE_DASHBOARD_RANGE,
  SAVE_PAGE,
  SAVE_TRANSACTIONS,
  SET_EXTERNAL_PAYMENT_PROVIDERS,
  SET_PAYOUT_TYPES,
  START_LOADING,
  STOP_LOADING
} from '@/store/keys'
import DatePicker from 'vue2-datepicker'
import UpdateTransactionStatusModal from '../components/modals/UpdateTransactionStatusModal.vue'
import UpdateTransactionLogsModal from '../components/modals/UpdateTransactionLogsModal'
import RevolutResultModal from '../components/modals/RevolutResultModal'
import BunqResultModal from '../components/modals/BunqResultModal'
import SquareResultModal from '../components/modals/SquareResultModal'
import StripeResultModal from '../components/modals/StripeResultModal'
import RevolutBankResultModal from '../components/modals/RevolutBankResultModal'
import RapydResultModal from '../components/modals/RapydResultModal'
import $ from 'jquery'
import mixins from '@/lib/mixins'

export default {
  name: 'Dashboard',
  components: {
    RapydResultModal,
    RevolutBankResultModal,
    StripeResultModal,
    SquareResultModal,
    BunqResultModal,
    RevolutResultModal,
    'va-info-box': VAInfoBox,
    Pagination,
    DatePicker,
    UpdateTransactionStatusModal,
    UpdateTransactionLogsModal
  },
  mixins: [mixins],
  data() {
    return {
      dashboardOverview24H: {
        TotalTransactions: 0,
        TotalPaidTransactions: 0,
        TotalVolume: 0,
        TotalPaidVolume: 0,
        TotalRevenueInEUR: 0,
        TotalRegistrations: 0
      },
      sync: '',
      transactionsList: [],
      recentRegistrations: [],
      transactionFailureCauses: null,
      pendingKYC: [],
      currentPage: 1,
      limit: Number.parseInt(process.env.VUE_APP_LIMIT, 10),
      keyword: '',
      status: '',
      merchantId: '',
      statusList: {
        '': 'All',
        waiting: 'Waiting',
        expired: 'Expired',
        paid: 'Paid',
        underpaid: 'Underpaid',
        'rt-t': 'Reflextrader Transaction',
        'rt-p': 'Reflextrader Payout',
        'vtoken-active': 'vToken Active',
        'vtoken-claimed': 'vToken Claimed',
        'ltr-open': 'LTR Open',
        'ltr-closed': 'LTR Closed',
        'ltr-expired': 'LTR Expired',
        'payout completed': 'Payout Completed',
        'payout pending': 'Payout Pending',
        'payout cancelled': 'Payout Cancelled'
      },
      reload: false,
      range: {
        startDate: this.tz().subtract(6, 'days').format('YYYY-MM-DD'),
        endDate: this.tz().format('YYYY-MM-DD')
      },
      sortKey: 'CreatedOn',
      order: 'desc',
      destroyed: false
    }
  },
  computed: {
    ...mapGetters([
      'isLoading',
      'jwtToken',
      'transactions',
      'page',
      'dashboardRange',
      'converter',
      'searchKeyword',
      'externalPaymentProviders',
      'payoutTypes',
      'altCoins'
    ]),
    filteredTransactions() {
      let formattedTransactions = this.transactionsList.filter(transaction => {
        const type = transaction.ExternalTransactionProvider ? transaction.ExternalTransactionProvider : transaction.TransactionType
        if (this.keyword) {
          let keyword = this.keyword.trim().toLowerCase()
          if (keyword.startsWith('xc')) {
            keyword = keyword.replace('xc', '')
            return transaction.XChangeID === keyword
          } else {
            if ((keyword.startsWith('t') || keyword.startsWith('p')) && isNaN(keyword.slice(1)) === false) {
              keyword = keyword.replace(/[a-z]/, '')
              keyword = Number.parseInt(keyword).toString()
            }
            return (this._.includes(transaction.ShopTitle.toLowerCase(), keyword) ||
              this._.includes(transaction.TransactionID.toString(), keyword) ||
              this._.includes(transaction.MerchantID.toString(), keyword) ||
              this._.includes(transaction.Security.toLowerCase(), keyword) ||
              this._.includes(transaction.Email.toLowerCase(), keyword) ||
              this._.includes(transaction.Comments && transaction.Comments.toLowerCase(), keyword) ||
              this._.includes(transaction.CoinName.toLowerCase(), keyword) ||
              this._.includes(transaction.LongName.toLowerCase(), keyword) ||
              this._.includes(transaction.CustomerReferenceNr && transaction.CustomerReferenceNr.toLowerCase(), keyword) ||
              this._.includes(this.toFixed(transaction.Amount).toString(), keyword) ||
              this._.includes(transaction.ConfirmURL && transaction.ConfirmURL.toLowerCase(), keyword) ||
              this._.includes(transaction.FailURL && transaction.FailURL.toLowerCase(), keyword) ||
              this._.includes(transaction.InvoiceCC && transaction.InvoiceCC.toLowerCase(), keyword) ||
              this._.includes(transaction.CoinAddress.toLowerCase(), keyword) ||
              this._.includes(this.toFixed(transaction.OriginalAmount).toString(), keyword) ||
              this._.includes(transaction.CreatedOn.toString(), keyword) ||
              this._.includes(type.toLowerCase(), keyword))
          }
        }
        return true
      })
      formattedTransactions = formattedTransactions.filter(transaction => {
        if (this.status && this.status !== 'all') {
          if (this.status === 'rt-t' || this.status === 'rt-p') {
            const prefix = this.status.split('-')[1]
            return transaction.Status === 'reflextrader' && transaction.Prefix.toLowerCase() === prefix
          } else {
            return transaction.Status === this.status
          }
        }
        return true
      })
      formattedTransactions = formattedTransactions.filter(transaction => {
        if (this.merchantId) {
          return transaction.MerchantID.toString() === this.merchantId.toString()
        }
        return true
      })
      return formattedTransactions
    },
    paginatedTransactions() {
      if (this.filteredTransactions.length > 0) {
        let filteredTransactions = this.filteredTransactions
        if (this.sortKey) {
          filteredTransactions = this._.orderBy(filteredTransactions, [this.sortKey], [this.order])
        }
        return filteredTransactions.slice(
          this.offset * this.limit,
          this.currentPage * this.limit
        )
      }
      return this.filteredTransactions
    },
    offset() {
      return this.currentPage > 0 ? this.currentPage - 1 : 0
    },
    meta() {
      return {
        total: this.filteredTransactions.length,
        offset: this.offset * this.limit,
        limit: this.limit,
        page: this.currentPage
      }
    },
    merchants() {
      const merchants = [
        {
          ID: '',
          Name: 'All'
        }
      ]
      this.transactionsList.forEach(t => {
        if (this._.findIndex(merchants, { ID: t.MerchantID }) === -1) {
          merchants.push({
            ID: t.MerchantID,
            Name: t.ShopTitle + ' (' + t.OwnerNickName + ')'
          })
        }
      })
      return merchants
    }
  },
  watch: {
    searchKeyword(val) {
      this.keyword = val
    },
    filteredTransactions(value) {
      if (this.keyword !== '' || this.status !== 'all') {
        this.updatePage(1)
      }
    },
    $route(to, from) {
      if (to.query.reload) {
        this.reload = true
        this.$router.replace({
          ...this.$router.currentRoute,
          query: {}
        })
        this.range = {
          startDate: this.tz().subtract(6, 'days').format('YYYY-MM-DD'),
          endDate: this.tz().format('YYYY-MM-DD')
        }
        this.getLatestInfo(this.range.startDate, this.range.endDate)
        this.getLast24HData()
        this.getTotalRegistrationIn24H()
        this.getRecentRegistrations()
        this.getLatestKYC()
      }
    }
  },
  methods: {
    resetPage() {
      const self = this
      self.keyword = ''
    },
    disabledDates(date) {
      return date > new Date()
    },
    toConverter(amount, altCoinID) {
      const form = { ...this.converter }
      form.source = amount
      form.sourceAltCoinId = altCoinID
      this.$store.commit(SAVE_CONVERTER, form)
      this.copyToClipboard(amount)
    },
    shortNameToID(shortName) {
      const coin = this._.find(this.altCoins, { ShortName: shortName })
      return coin ? coin.ID : 625
    },
    copyToClipboard(val, isInvoice = false) {
      if (isInvoice) {
        val = 'https://cointopay.com/invoice/' + val
      }
      const self = this
      navigator.clipboard.writeText(val).then(function() {
        self.$toast.fire('', 'Copied to clipboard: ' + val, 'success')
        saveNotification('Copied to clipboard: ' + val)
      }, function(err) {
        console.error('Async: Could not copy text: ', err)
      })
    },
    updatePage(page) {
      this.currentPage = page
    },
    updatePageTF(page) {
      this.getTransactionFailureCauses(page)
    },
    fs(status) {
      return formatStatus(status)
    },
    fd(val) {
      return formatDate(val)
    },
    dateDiffInDays(d1, d2) {
      return dateDiffInDays(d1, d2, false)
    },
    toFixed(val) {
      return toFixed(val)
    },
    sort(key) {
      if (key !== this.sortKey) {
        this.order = 'desc'
      } else {
        this.order = this.order === 'asc' ? 'desc' : 'asc'
      }
      this.sortKey = key
    },
    getPaymentProviders() {
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=ListMisc&type=payment_providers`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        }
      }).then(response => {
        const data = response.data.data
        this.$store.commit(SET_EXTERNAL_PAYMENT_PROVIDERS, data.externalPaymentProviders)
        this.$store.commit(SET_PAYOUT_TYPES, data.payoutTypes)
      }).catch(error => {
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
          saveNotification(error.response.data)
        }
      })
    },
    getLatestKYC() {
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=LatestKYC`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        }
      }).then(response => {
        this.pendingKYC = response.data.data
      }).catch(error => {
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
          saveNotification(error.response.data)
        }
      })
    },
    getLast24HData() {
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=Last24HData`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        }
      }).then(response => {
        const data = response.data.data
        this.dashboardOverview24H.TotalTransactions = data.TotalTransactions
        this.dashboardOverview24H.TotalPaidTransactions = data.TotalPaidTransactions
        this.dashboardOverview24H.TotalVolume = data.TotalVolume
        this.dashboardOverview24H.TotalPaidVolume = data.TotalPaidVolume
        this.dashboardOverview24H.TotalRevenueInEUR = data.TotalRevenueInEUR
      }).catch(error => {
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
        }
      })
    },
    load24HData() {
      this.start = this.tz().subtract(1, 'days').format('YYYY-MM-DD')
      this.end = this.tz().format('YYYY-MM-DD')
      this.transactionsList = []
      this.range = {
        startDate: this.start,
        endDate: this.end
      }
      this.getLatestInfo(this.start, this.end, true)
    },
    getLatestInfo(start, end, isLoading = true) {
      start = start.replace(' 00:00:00', '')
      end = end.replace(' 23:59:59', '')
      if (isLoading) {
        this.transactionsList = []
        this.$store.commit(START_LOADING)
      }
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=LatestInfo`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        },
        params: {
          startDate: start + ' 00:00:00',
          endDate: end + ' 23:59:59'
        }
      }).then(response => {
        if (response.status === 200) {
          this.$store.commit(STOP_LOADING)
          if (this.transactionsList.length === 0) {
            this.transactionsList = response.data.data.transactions
          } else {
            this.transactionsList = this.transactionsList.concat(response.data.data.transactions)
          }
          if (!this.destroyed) {
            this.timeout = setTimeout(() => {
              this.$refs.search.focus()
            }, 50)
            this.transactionsList.sort(function compare(a, b) {
              const dateA = new Date(a.CreatedOn)
              const dateB = new Date(b.CreatedOn)
              return dateB - dateA
            })
            start = typeof response.data.data.startDate !== 'undefined' ? response.data.data.startDate : null
            end = typeof response.data.data.endDate !== 'undefined' ? response.data.data.endDate : null
            if (start !== null && end !== null) {
              this.getLatestInfo(start, end, false)
              this.sync = '<i class="fa fa-refresh fa-spin mr-8"></i>Syncing...'
            } else {
              this.sync = ''
              console.log('transactionsList length:' + this.transactionsList.length)
              this.$store.commit(SAVE_TRANSACTIONS, this.transactionsList)
            }
          }
        }
      }).catch(error => {
        this.$store.commit(STOP_LOADING)
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
        }
      })
    },
    getRecentRegistrations() {
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=RecentRegistrations`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        }
      }).then(response => {
        this.recentRegistrations = response.data.data
      }).catch(error => {
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
        }
      })
    },
    getTotalRegistrationIn24H() {
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=TotalRegistrations24H`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        }
      }).then(response => {
        this.dashboardOverview24H.TotalRegistrations = response.data.data
      }).catch(error => {
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
        }
      })
    },
    getTransactionFailureCauses(page = 1) {
      const apiBaseUrl = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${apiBaseUrl}/?Call=TransactionFailure`, {
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        },
        params: {
          page,
          limit: 5
        }
      }).then(response => {
        this.transactionFailureCauses = response.data.data
      }).catch(error => {
        if (error.data.message.toLowerCase() !== 'unauthorized') {
          this.$toast.fire('', error.data.message, 'error')
        }
      })
    },
    savePage() {
      this.$store.commit(SAVE_PAGE, this.currentPage)
    },
    searchLatestInfo() {
      if (this.range.startDate !== '' && this.range.endDate !== '') {
        if (this.dateDiffInDays(new Date(this.range.startDate), new Date(this.range.endDate)) >= 0) {
          if (this.dateDiffInDays(new Date(this.range.endDate), new Date()) >= 0) {
            this.$store.commit(SAVE_DASHBOARD_RANGE, this.range)
            this.getLatestInfo(this.range.startDate, this.range.endDate)
          } else {
            this.$toast.fire('', 'To date can not be greater than today', 'warning')
          }
        }
      }
    },
    getTransaction(id) {
      const url = process.env.VUE_APP_ADMIN_API_URL
      this.$http.get(`${url}/?Call=TransactionDetail`, {
        params: {
          ID: id
        },
        headers: {
          Authorization: 'Bearer ' + this.jwtToken
        }
      }).then(response => {
        if (response.status === 200) {
          const data = response.data.data
          // trim char part from id and convert it to number
          id = id.replace(/[a-z]/i, '')
          id = Number(id)
          const index = this._.findIndex(this.transactions, { TransactionID: id })
          if (index !== -1) {
            let transaction = this.transactions[index]
            transaction = Object.assign({}, transaction, {
              Status: data.Status
            })
            this.transactions.splice(index, 1, transaction)
          }
        }
      }).catch(error => {
        if (error && error.data && error.data.message) {
          if (error.data.message.toLowerCase() !== 'unauthorized') {
            this.$toast.fire('', error.data.message, 'error')
            saveNotification(error.data.message)
          }
        }
      })
    },
    launchTransactionStatusModal(transaction) {
      this.$bus.$emit('update-transaction-status-modal', {
        title: 'Update Transaction Status',
        data: transaction,
        confirm: () => {
          const id = transaction.Prefix + transaction.TransactionID.toString().padStart(9, '0')
          this.getTransaction(id)
        }
      })
    },
    launchUpdateTransactionLogsModal(transaction, index) {
      this.$bus.$emit('update-transaction-logs-modal', {
        title: 'Update Transaction Logs',
        data: transaction,
        confirm: (comments, paymentProviderID) => {
          const data = (transaction.Prefix === 'T') ? this.externalPaymentProviders : this.payoutTypes
          const pp = this._.find(data, { ID: paymentProviderID })
          if (pp) {
            const index = this._.findIndex(this.transactions, { TransactionID: transaction.TransactionID })
            if (index !== -1) {
              transaction = Object.assign({}, transaction, {
                ExternalTransactionProvider: pp.Name,
                Comments: comments
              })
              this.transactions.splice(index, 1, transaction)
            }
          }
        }
      })
    },
    getRevolutTransactionDetail(confirmCode) {
      const url = 'https://tippr.today:9443/invoice-payment/?call=orderDetail&confirmCode=' + confirmCode
      this.$http.get(`${url}`).then(response => {
        this.$bus.$emit('revolut-result-modal', {
          title: 'Detail',
          data: response.data.data
        })
      }).catch(error => {
        console.log(error)
      })
    },
    getRapydTransactionDetail(transaction) {
      const url = `https://tippr.today:9443/rapyd/?action=confirmitjson&ID=${transaction.revolutID}&amount=${transaction.OriginalAmount}&confirm_code=${transaction.Security}`
      this.$http.get(`${url}`).then(response => {
        this.$bus.$emit('rapyd-result-modal', {
          title: 'Detail',
          message: response.data.message
        })
      }).catch(error => {
        this.$bus.$emit('rapyd-result-modal', {
          title: 'Detail',
          message: error.data.message
        })
      })
    },
    getPayrexxTransactionDetail(transaction) {
      const url = `https://tippr.today:9443/payrexx/?action=retrieve&confirm_code=${transaction.Security}&id=${transaction.revolutID}`
      this.$http.get(`${url}`).then(response => {
        this.$bus.$emit('rapyd-result-modal', {
          title: 'Detail',
          message: response.data.message
        })
      }).catch(error => {
        this.$bus.$emit('rapyd-result-modal', {
          title: 'Detail',
          message: error.data.message
        })
      })
    },
    getType(transaction) {
      return transaction.ExternalTransactionProvider ? transaction.ExternalTransactionProvider : transaction.TransactionType
    },
    getRevolutBankTransactionDetail(transaction, isReference = false) {
      const id = !isReference ? transaction.TransactionID : transaction.CustomerReferenceNr
      const url = 'https://app.cointopay.com/revolut?call=transactions&api_key=AABB9fCDE93kd93aakk378djddmcmcnvd483982048272987498789472984792&reference=' + id
      this.$http.get(`${url}`).then(response => {
        this.$bus.$emit('revolut-bank-result-modal', {
          title: 'Detail',
          data: response.data.data
        })
      }).catch(error => {
        if (error.status === 404 && error.data.message === 'No payment information found' && !isReference) {
          this.getRevolutBankTransactionDetail(transaction, true)
        } else {
          this.$bus.$emit('revolut-bank-result-modal', {
            title: 'Detail',
            data: [],
            message: error.data.message
          })
        }
      })
    },
    getBunqTransactionDetail(transaction) {
      const apiKey = '2d743e48c8a03bea86a8d38858b91398fdf3b6d7d38ce6f79034ab61bae8a8365ba9336fd79cf192ed5028d3555621722d743e48c8a03bea86a8d38858b91398fdf3b6d7d38ce6f79034ab61bae8a83693cc208ccb46d66f3fb009932152e6a4'
      const url = `https://app.cointopay.com/bunq?action=receive&api_key=${apiKey}&message=${transaction.TransactionID}&amount=${transaction.OriginalAmount.toFixed(2)}`
      this.$http.get(`${url}`, {
        withCredentials: true
      }).then(response => {
        this.$bus.$emit('bunq-result-modal', {
          title: 'Detail',
          data: response.data.map ? response.data.map : null,
          message: ''
        })
      }).catch(error => {
        this.$bus.$emit('bunq-result-modal', {
          title: 'Detail',
          data: null,
          message: error.data.message
        })
        console.log(error.data.message)
      })
    },
    getSquareTransactionDetail(transaction) {
      const url = 'https://tippr.today:9443/sup/?Call=searchById&id=' + transaction.revolutID
      this.$http.get(`${url}`, {
        withCredentials: true
      }).then(response => {
        this.$bus.$emit('square-result-modal', {
          title: 'Detail',
          data: response.data.data ? response.data.data : null,
          message: ''
        })
      }).catch(error => {
        this.$bus.$emit('square-result-modal', {
          title: 'Detail',
          data: null,
          message: error.data.message
        })
        console.log(error.data.message)
      })
    },
    getStripeTransactionDetail(transaction) {
      const url = 'https://ebrighter.com:9443/ctpv2/?call=search&id=' + transaction.revolutID + '&currency=EUR'
      this.$http.get(`${url}`, {
        withCredentials: true
      }).then(response => {
        this.$bus.$emit('stripe-result-modal', {
          title: 'Detail',
          data: response.data.data ? response.data.data : null,
          message: ''
        })
      }).catch(error => {
        this.$bus.$emit('stripe-result-modal', {
          title: 'Detail',
          data: null,
          message: 'No record found!'
        })
        console.log(error.data.message)
      })
    }
  },
  mounted() {
    let isSearching = false
    if (this.$route.query.searching) {
      isSearching = true
      this.keyword = this.searchKeyword
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {}
      })
    }
    if (this.$route.query.reload) {
      this.reload = true
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {}
      })
    }
    if (this.$route.query.xc) {
      this.keyword = this.$route.query.xc
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {}
      })
      this.range = {
        startDate: this.tz().subtract(60, 'days').format('YYYY-MM-DD'),
        endDate: this.tz().format('YYYY-MM-DD')
      }
      this.transactionsList = []
      this.$store.commit(SAVE_TRANSACTIONS, this.transactionsList)
    }
    if (this.page && this.transactions.length > 0 && !this.reload) {
      this.updatePage(this.page)
      this.transactionsList = this.transactions
      this.$store.commit(STOP_LOADING)
      if (this.dashboardRange) {
        this.range = this.dashboardRange
      }
    } else {
      this.getLatestInfo(this.range.startDate, this.range.endDate)
    }
    this.getLast24HData()
    this.getLatestKYC()
    this.getRecentRegistrations()
    this.getTotalRegistrationIn24H()
    this.getTransactionFailureCauses()

    // Get roles
    this.$store.dispatch(ROLES).catch(() => {
    })
    if (!isSearching) {
      // document.getElementById('search').focus()
    }
    this.getPaymentProviders()
    const self = this
    $(document).on('keydown', function(e) {
      if (e.key === 'F2') {
        self.range.endDate = self.tz().format('YYYY-MM-DD')
        self.getLast24HData()
        self.getLatestKYC()
        self.getRecentRegistrations()
        self.getTotalRegistrationIn24H()
        self.getTransactionFailureCauses()
        document.getElementById('search').focus()
        return ''
      }
    })
  },
  beforeDestroy() {
    this.destroyed = true
  }
}
</script>

<style scoped lang="scss">
.text-dark-gray {
  color: #878889;
}
</style>
